/* Boutique : panier, tiroir panier, passage en caisse, compte client.
   Démonstration (site HTML) : aucun paiement réel ni envoi de données, tout reste dans le navigateur (localStorage).
   Le catalogue vient de assets/data/catalog.js (window.CG_SHOP), généré par « npm run shop ».
   Thème Shopify (« npm run shopify ») : CG_SHOP est écrit par le snippet cg-catalog (shopify: true),
   le panier passe par l'API AJAX de Shopify (/cart.js…) et la caisse est celle de Shopify. */
(() => {
  const SHOP = window.CG_SHOP;
  if (!SHOP) return;
  const SHOPIFY = !!SHOP.shopify;
  const ROUTES = { cart: 'cart.html', checkout: 'checkout.html', collection: 'collections-all.html', account: 'account.html', ...(SHOP.routes || {}) };
  // shop --couleur-panier : couleur des boutons et repères du panier (vert de l'original remplacé), texte lisible dessus
  const ACCENT = SHOP.drawer && /^#[0-9a-f]{6}$/i.test(SHOP.drawer.accent || '') ? SHOP.drawer.accent : '';
  if (ACCENT) {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(ACCENT.slice(i, i + 2), 16));
    const sombre = 0.299 * r + 0.587 * g + 0.114 * b < 140;
    const style = document.createElement('style');
    style.textContent = `.cg-drawer, [data-cart-page] { --cgd-accent: ${ACCENT}; --cgd-accent-ink: ${sombre ? ACCENT : '#000'}; --cgd-on-accent: ${sombre ? '#fff' : '#000'}; --cgd-frame: ${ACCENT}; }`;
    document.head.appendChild(style);
  }
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ---------------------------------------------------------------------
     Stockage (try/catch : navigation privée, fichiers locaux)
     --------------------------------------------------------------------- */
  const memory = {};
  const store = {
    get(key, fallback) {
      try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return key in memory ? memory[key] : fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { memory[key] = value; }
    },
  };

  /* ---------------------------------------------------------------------
     Prix : centimes → « €26,99 » (tiroir, comme l'original) ou « 26,99 € » (caisse)
     --------------------------------------------------------------------- */
  const amount = (c) => (c / 100).toFixed(2).replace('.', ',');
  // format du site si « npm run shop » l'a relevé (« {amount} € », « €{amount_dot} »…)
  // Shopify (snippet cg-catalog) : format de la boutique, « {{amount_with_comma_separator}} zł », « ${{amount}} »…
  const fmt = (c) => SHOP.moneyFormat
    .replace(/\{\{\s*(amount\w*)\s*\}\}/, (_m, k) => (/no_decimals/.test(k) ? String(Math.round(c / 100)) : /comma/.test(k) ? amount(c) : amount(c).replace(',', '.')))
    .replace(/\{amount(_dot)?\}/, (_m, dot) => (dot ? amount(c).replace(',', '.') : amount(c)));
  const money = (c) => (SHOP.moneyFormat ? fmt(c) : `€${amount(c)}`);
  const euros = (c) => (SHOP.moneyFormat ? fmt(c) : `${amount(c)} €`);
  // montant rond sans centimes (« €25 », « 290 zł ») : jalons des paliers
  const moneyShort = (c) => (c % 100 ? money(c) : money(c).replace(/[,.]00(?!\d)/, ''));

  /* ---------------------------------------------------------------------
     Textes de l'interface (français par défaut) ; site traduit : CG_SHOP.texts,
     écrit par « npm run shop » dans la langue du site. {x} = valeur insérée.
     --------------------------------------------------------------------- */
  const TXT = {
    cartCount: 'Nombre total d’articles dans le panier: {n}',
    allUnlocked: 'Livraison et produit offerts',
    giftLeft: 'Plus que {montant} pour votre produit offert',
    shippingLeft: 'Plus que {montant} pour la livraison offerte',
    tiers: 'Paliers du panier',
    freeShipping: 'Livraison offerte',
    gift: 'Produit offert',
    regularPrice: 'Prix régulier',
    salePrice: 'Prix promotionnel',
    frequency: 'Fréquence de livraison',
    oneTime: 'Repasser en achat unique',
    subscribe: 'S’abonner et économiser {pct} %',
    decrease: 'Diminuer la quantité de {produit}',
    qtyOf: 'Quantité de {produit}',
    increase: 'Augmenter la quantité de {produit}',
    remove: 'Supprimer {produit}',
    upsell: 'Pour compléter',
    addProduct: 'Ajouter {produit} au panier',
    addToCart: 'Ajouter au panier',
    empty: 'Votre panier est vide',
    continueShopping: 'Continuer les achats',
    estimated: 'Total estimé',
    checkout: 'Passer ma commande',
    taxNote: 'Taxes incluses. Frais d’expédition calculés à l’étape de paiement.',
    cart: 'Panier',
    perk: 'livraison offerte avec l’abonnement',
    perkFrom: 'livraison offerte dès {montant}',
    subOfferTitle: 'Abonnez-vous et économisez {pct} % à chaque livraison.',
    subOfferNote: 'Sans engagement, annulable à tout moment.',
    subOfferButton: 'Je m’abonne',
    close: 'Fermer le panier',
    added: 'Ajouté',
    unavailable: 'Indisponible',
    discountCode: 'Code de réduction',
    subtotal: 'Sous-total',
    item: 'article',
    items: 'articles',
    shipping: 'Expédition',
    free: 'GRATUIT',
    total: 'Total',
    discountInvalid: 'Saisissez un code de réduction valide (aucun code actif en démonstration)',
    discountEmpty: 'Saisissez un code de réduction',
    payCard: 'Payer maintenant',
    payPaypal: 'Payer avec PayPal',
    payKlarna: 'Continuer avec Klarna',
    emailInvalid: 'Saisissez une adresse e-mail valide',
    emailEmpty: 'Saisissez une adresse e-mail',
    lastName: 'Saisissez un nom',
    address: 'Saisissez une adresse',
    postal: 'Saisissez un code postal',
    postalInvalid: 'Saisissez un code postal valide pour {pays}',
    city: 'Saisissez une ville',
    phone: 'Saisissez un numéro de téléphone valide',
    cardEmpty: 'Saisissez un numéro de carte',
    cardInvalid: 'Saisissez un numéro de carte valide',
    expiryInvalid: 'Saisissez une date d’expiration valide',
    expiryEmpty: 'Saisissez une date d’expiration',
    expired: 'La carte a expiré',
    cvcInvalid: 'Saisissez un code de sécurité valide',
    cvcEmpty: 'Saisissez le code de sécurité',
    cardName: 'Saisissez le nom figurant sur la carte',
    orderNumber: 'Commande #{n}',
    code: 'Saisissez le code à 6 chiffres',
    confirmed: 'Confirmée',
    noOrders: 'Vous n’avez pas encore passé de commande.',
    startShopping: 'Commencer vos achats',
    noAddress: 'Aucune adresse enregistrée.',
    required: 'Complétez les champs obligatoires.',
    addressSaved: 'Adresse enregistrée. Elle sera proposée au passage en caisse.',
    chooseOption: 'Choisissez une option avant d’ajouter au panier.',
  };
  const T = Object.assign({}, TXT, SHOP.texts || {});
  const tx = (key, values = {}) => T[key].replace(/\{(\w+)\}/g, (m, k) => (k in values ? String(values[k]) : m));

  /* ---------------------------------------------------------------------
     Catalogue
     --------------------------------------------------------------------- */
  // nom court des formules d'abonnement (tiroir) : absent du catalogue Shopify, déduit du nom
  const PLAN_SHORT = [[/2 semaines|15 jours/i, 'Tous les 15 jours'], [/2 mois/i, 'Tous les 2 mois'], [/mensuel|mois/i, 'Tous les mois']];
  SHOP.products.forEach((p) => p.plans.forEach((pl) => { if (!pl.short) pl.short = (PLAN_SHORT.find(([re]) => re.test(pl.name)) || [0, pl.name])[1]; }));
  const byHandle = new Map(SHOP.products.map((p) => [p.handle, p]));
  // produits « pack » des offres à article remisé (une variante par combinaison de modèles), hors listes de produits
  const PACKS = SHOP.packs || [];
  const findVariant = (id) => {
    for (const p of SHOP.products) {
      const v = p.variants.find((x) => String(x.id) === String(id));
      if (v) return { product: p, variant: v };
    }
    return null;
  };
  const variantLabel = (v) => v.options.filter((o) => o !== 'Default Title').join(', ');
  const planOf = (product, id) => (id ? product.plans.find((p) => String(p.id) === String(id)) : null);
  const unitPrice = (variant, plan) => (plan ? Math.round(variant.price * (1 - plan.discount / 100)) : variant.price);

  /* ---------------------------------------------------------------------
     Panier : [{ key, variantId, qty, planId }]
     Démonstration : localStorage. Shopify : état du panier Shopify gardé en mémoire,
     chaque action passe par /cart/add.js ou /cart/change.js (requêtes en file).
     Les actions rendent une promesse (rejetée si Shopify refuse : rupture de stock…).
     --------------------------------------------------------------------- */
  const CART_KEY = 'cg-cart';
  const listeners = new Set();
  const notify = () => listeners.forEach((fn) => fn());
  // Shopify coupe une ligne en deux quand une réduction « Achetez X, obtenez Y » ne porte que sur une partie de la quantité :
  // lignes de même variante, formule et cadeau regroupées (quantité et prix additionnés, toutes leurs clés gardées)
  const fromShopify = (c) => ((c && c.items) || []).reduce((lines, i) => {
    const line = {
      key: i.key,
      keys: [i.key],
      variantId: String(i.variant_id),
      qty: i.quantity,
      planId: i.selling_plan_allocation ? String(i.selling_plan_allocation.selling_plan.id) : null,
      final: i.final_line_price,
      gift: !!(i.properties && i.properties._cg_gift),
    };
    const same = lines.find((l) => l.variantId === line.variantId && l.planId === line.planId && l.gift === line.gift);
    if (same) { same.keys.push(i.key); same.qty += line.qty; same.final += line.final; } else lines.push(line);
    return lines;
  }, []);
  let remote = SHOPIFY ? fromShopify(SHOP.cart) : null;
  let queue = Promise.resolve();
  // body peut être une fonction : évaluée au départ de la requête (après celles en attente)
  const call = (url, body) => {
    const run = () => fetch(url, body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(typeof body === 'function' ? body() : body) }
      : { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.description || data.message || `HTTP ${r.status}`);
        return data;
      });
    const p = queue.then(run, run);
    queue = p.catch(() => {});
    return p;
  };
  const sync = (c) => { remote = fromShopify(c); notify(); return c; };
  // Shopify change la clé d'une ligne quand une réduction s'y applique (produit offert) :
  // une action en attente retrouve sa ligne par variante, formule et cadeau, pas par une clé périmée
  const identity = (l) => `${l.variantId}:${l.planId || ''}:${l.gift ? 1 : 0}`;
  const keyNow = (line) => ((remote || []).find((l) => identity(l) === identity(line)) || line).key;
  const refresh = () => call(`${ROUTES.cart}.js`).then(sync);
  const addItems = (variantId, qty, planId) => call(`${ROUTES.cartAdd}.js`, { items: [{ id: Number(variantId), quantity: qty, ...(planId ? { selling_plan: Number(planId) } : {}) }] });
  const addManyItems = (items) => call(`${ROUTES.cartAdd}.js`, { items: items.map((i) => ({ id: Number(i.variantId), quantity: i.qty })) });
  const cart = {
    lines() {
      return cart.raw().map((l) => {
        const found = findVariant(l.variantId);
        if (!found) return null;
        const plan = planOf(found.product, l.planId);
        const unit = unitPrice(found.variant, plan);
        // Shopify : prix final calculé par Shopify (abonnement, réductions automatiques)
        return { ...l, ...found, plan, unit, total: typeof l.final === 'number' ? l.final : unit * l.qty, compare: found.variant.price * l.qty };
      }).filter(Boolean).sort((a, b) => Number(a.gift || 0) - Number(b.gift || 0)); // produit offert en dernier
    },
    save(raw) {
      store.set(CART_KEY, raw);
      notify();
    },
    raw: () => remote || store.get(CART_KEY, []),
    // offre groupée : plusieurs produits en une requête
    addMany(items) {
      if (items.some((i) => !findVariant(i.variantId))) return Promise.reject(new Error('variante inconnue'));
      if (remote) return addManyItems(items).then(refresh);
      return items.reduce((p, i) => p.then(() => cart.add(i.variantId, i.qty)), Promise.resolve());
    },
    add(variantId, qty = 1, planId = null) {
      if (!findVariant(variantId)) return Promise.reject(new Error('variante inconnue'));
      if (remote) return addItems(variantId, qty, planId).then(refresh);
      const raw = cart.raw();
      const key = `${variantId}:${planId || ''}`;
      const line = raw.find((l) => l.key === key);
      if (line) line.qty = Math.min(99, line.qty + qty);
      else raw.push({ key, variantId: String(variantId), qty, planId: planId ? String(planId) : null });
      cart.save(raw);
      return Promise.resolve();
    },
    setQty(key, qty) {
      if (remote) {
        const line = remote.find((l) => l.key === key);
        if (!line) return refresh();
        const q = Math.max(0, Math.min(99, qty));
        // ligne regroupée (coupée par Shopify) : toute la quantité sur sa première clé, les autres à 0
        const now = () => (remote || []).find((l) => identity(l) === identity(line)) || line;
        if (line.keys.length > 1 && ROUTES.cartUpdate) return call(`${ROUTES.cartUpdate}.js`, () => ({ updates: Object.fromEntries(now().keys.map((k, i) => [k, i ? 0 : q])) })).then(refresh).catch(refresh);
        return call(`${ROUTES.cartChange}.js`, () => ({ id: keyNow(line), quantity: q })).then(sync).catch(refresh);
      }
      const raw = cart.raw();
      const line = raw.find((l) => l.key === key);
      if (!line) return Promise.resolve();
      if (qty <= 0) raw.splice(raw.indexOf(line), 1);
      else line.qty = Math.min(99, qty);
      cart.save(raw);
      return Promise.resolve();
    },
    setPlan(key, planId) {
      if (remote) {
        const line = remote.find((l) => l.key === key);
        if (!line) return Promise.resolve();
        const want = String(planId || '');
        return call(`${ROUTES.cartChange}.js`, () => ({ id: keyNow(line), quantity: line.qty, selling_plan: planId ? Number(planId) : null }))
          .catch(() => null)
          .then((c) => {
            if (c && fromShopify(c).some((l) => l.variantId === line.variantId && String(l.planId || '') === want)) return sync(c);
            // formule non modifiable par /cart/change.js : on retire la ligne puis on la rajoute
            return call(`${ROUTES.cartChange}.js`, () => ({ id: keyNow(line), quantity: 0 })).then(() => addItems(line.variantId, line.qty, planId)).then(refresh);
          })
          .catch(refresh);
      }
      const raw = cart.raw();
      const line = raw.find((l) => l.key === key);
      if (!line) return Promise.resolve();
      const newKey = `${line.variantId}:${planId || ''}`;
      const other = raw.find((l) => l.key === newKey && l !== line);
      if (other) { other.qty += line.qty; raw.splice(raw.indexOf(line), 1); }
      else Object.assign(line, { key: newKey, planId: planId ? String(planId) : null });
      cart.save(raw);
      return Promise.resolve();
    },
    clear: () => (remote ? call(`${ROUTES.cart}/clear.js`, {}).then(sync) : (cart.save([]), Promise.resolve())),
    count: () => cart.raw().reduce((n, l) => n + l.qty, 0),
    totals() {
      const lines = cart.lines();
      const subtotal = lines.reduce((n, l) => n + l.total, 0);
      const compare = lines.reduce((n, l) => n + l.compare, 0);
      const subscription = lines.some((l) => l.plan);
      // seuil 0 (shop --livraison-offerte 0) : livraison jamais offerte
      // livraison offerte pour des formules précises (<site>/livraison-offerte.json) : seulement si tout le panier en est (comme les profils d'expédition de Shopify : un autre article paie les frais)
      const FREE = SHOP.freeShippingFor || [];
      const allFree = lines.length > 0 && FREE.length > 0 && lines.every((l) => FREE.some((f) => f.handle === l.product.handle && String(f.variant).toLowerCase() === String(l.variant.title || l.variant.option1 || '').toLowerCase()));
      const freeShipping = allFree || (SHOP.freeShippingFrom > 0 && (subscription || subtotal >= SHOP.freeShippingFrom));
      const shipping = lines.length && !freeShipping ? SHOP.shippingPrice : 0;
      return { lines, subtotal, compare, subscription, freeShipping, shipping, total: subtotal + shipping };
    },
    onChange: (fn) => listeners.add(fn),
  };
  /* Produits offerts (Shopify, réglages du thème « Produit offert », « Second produit offert ») : un exemplaire de chacun
     ajouté au panier dès le palier, retirés en dessous ; la réduction automatique « Achetez X, obtenez Y » les rend gratuits. */
  const GIFTS = SHOPIFY ? (SHOP.gifts || (SHOP.gift ? [SHOP.gift] : [])).map((h) => SHOP.products.find((p) => p.handle === h)).filter(Boolean) : [];
  const GIFT = GIFTS[0] || null;
  let giftBusy = false;
  const syncGift = () => {
    if (!GIFT || giftBusy || !remote) return;
    const lines = cart.lines();
    const gifts = lines.filter((l) => l.gift);
    const subtotal = lines.filter((l) => !l.gift).reduce((n, l) => n + l.total, 0);
    // jamais de cadeau sans article payant (palier 0 : offert dès le premier achat)
    const earned = subtotal > 0 && subtotal >= SHOP.giftFrom;
    const ofGift = (g) => gifts.filter((l) => g.variants.some((v) => String(v.id) === l.variantId));
    const missing = earned ? GIFTS.filter((g) => !ofGift(g).length && g.variants.some((v) => v.available)) : [];
    // de trop : tout sous le palier ; au-delà, chaque cadeau en un exemplaire, rien d'autre
    const extra = gifts.find((l) => !earned || !GIFTS.some((g) => ofGift(g)[0] === l) || l.qty > 1);
    let job = null;
    if (missing.length) {
      job = call(`${ROUTES.cartAdd}.js`, { items: missing.map((g) => ({ id: Number(g.variants.find((v) => v.available).id), quantity: 1, properties: { _cg_gift: '1' } })) }).then(refresh);
    } else if (extra) {
      job = call(`${ROUTES.cartChange}.js`, () => ({ id: keyNow(extra), quantity: earned && GIFTS.some((g) => ofGift(g)[0] === extra) ? 1 : 0 })).then(sync);
    }
    // une action à la fois (ajout groupé ou retrait d'une ligne) : on recommence après une réussite, jusqu'à ce qu'il n'y ait plus rien à faire
    if (job) { giftBusy = true; job.then(() => { giftBusy = false; syncGift(); }, () => { giftBusy = false; }); }
  };
  if (GIFT) listeners.add(syncGift);

  if (SHOPIFY) {
    // panier modifié dans un autre onglet, ou page revenue du cache (retour arrière)
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh().catch(() => {}); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh().catch(() => {}); });
  } else window.addEventListener('storage', (e) => { if (e.key === CART_KEY) notify(); });

  /* ---------------------------------------------------------------------
     Compteur du header
     --------------------------------------------------------------------- */
  // pastille du compteur : ajoutée à l'icône panier de l'en-tête si la reconstruction n'en a pas
  // icônes de l'en-tête prévues par la reconstruction sans destination (boutons data-cart-toggle / data-account-toggle) : panier et compte de la boutique
  $$('[data-cart-toggle]').forEach((b) => b.setAttribute('data-cart-open', ''));
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-account-toggle]');
    if (!a || a.closest('.cg-drawer')) return;
    e.preventDefault();
    location.href = 'account.html';
  });
  // compteur déjà prévu par la reconstruction ([data-cart-count]) : c'est lui la pastille, pas un second
  $$('[data-cart-count]').forEach((b) => b.classList.add('cart-bubble'));
  $$('[data-cart-open]').filter((a) => a.closest('header, .site-header, [data-header]') && !a.querySelector('.cart-bubble')).forEach((a) => {
    a.insertAdjacentHTML('beforeend', '<span class="cart-bubble visually-hidden"></span>');
  });
  const renderBubble = () => {
    const n = cart.count();
    // compteur d'un bouton panier recopié de l'original (bouton flottant…)
    $$('[data-cart-count]').forEach((b) => { b.textContent = String(n); });
    $$('.cart-bubble').forEach((b) => {
      b.classList.toggle('visually-hidden', n === 0);
      if (b.hasAttribute('data-cart-count')) b.setAttribute('data-count', String(n));
      b.innerHTML = n ? `<span aria-hidden="true">${n}</span><span class="visually-hidden">${tx('cartCount', { n })}</span>` : tx('cartCount', { n: 0 });
    });
  };

  /* ---------------------------------------------------------------------
     Rendu commun du panier (tiroir et page panier)
     --------------------------------------------------------------------- */
  // icônes du tiroir (trait 1,5 px, comme l'original)
  const icon = {
    close: '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M12 2L2 12M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    minus: '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    plus: '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.75 7H11.25M7 2.75V11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    trash: '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path class="cg-trash__bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path class="cg-trash__top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    check: '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    chevron: '<svg viewBox="0 0 10 6" fill="none" aria-hidden="true"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  // palier « produit offert » : seuil > 0 (shop --produit-offert 0 = livraison seule) et, sur Shopify, produit choisi
  const hasGift = () => SHOP.giftFrom > 0 && (!SHOPIFY || !!SHOP.gift);
  const progressHtml = (t) => {
    // ni livraison offerte ni produit offert : pas de paliers
    if (!(SHOP.freeShippingFrom > 0) && !hasGift()) return '';
    const { subtotal, subscription } = t;
    const shipOk = subscription || subtotal >= SHOP.freeShippingFrom;
    // cadeau offert dès le premier achat (palier sous celui de la livraison) : pas un palier de la barre, seulement sa ligne au panier
    const gift = hasGift() && SHOP.giftFrom > SHOP.freeShippingFrom;
    if (!(SHOP.freeShippingFrom > 0) && !gift) return '';
    const giftOk = gift && subtotal >= SHOP.giftFrom;
    // jalons à 25 % et 75 % de la barre, comme l'original (livraison seule : jalon au bout)
    const fill = !gift
      ? Math.min(1, subtotal / SHOP.freeShippingFrom)
      : giftOk ? 1 : subtotal >= SHOP.freeShippingFrom
        ? 0.25 + 0.5 * ((subtotal - SHOP.freeShippingFrom) / (SHOP.giftFrom - SHOP.freeShippingFrom))
        : 0.25 * (subtotal / SHOP.freeShippingFrom);
    const msg = giftOk
      ? T.allUnlocked
      : gift && shipOk
        ? tx('giftLeft', { montant: `<strong>${money(SHOP.giftFrom - subtotal)}</strong>` })
        : shipOk ? T.freeShipping : tx('shippingLeft', { montant: `<strong>${money(SHOP.freeShippingFrom - subtotal)}</strong>` });
    const step = (ok, price, label, pos) => `<li class="cg-progress__step${ok ? ' is-done' : ''}" style="--pos:${pos}"><span class="cg-progress__dot">${icon.check}</span><span class="cg-progress__amount">${moneyShort(price)}</span><span class="cg-progress__label">${label}</span></li>`;
    return `<div class="cg-progress"><p class="cg-progress__msg">${msg}</p>
      <div class="cg-progress__track"><div class="cg-progress__bar" role="progressbar" aria-valuemin="0" aria-valuemax="${(gift ? SHOP.giftFrom : SHOP.freeShippingFrom) / 100}" aria-valuenow="${(subtotal / 100).toFixed(2)}" aria-label="${esc(T.tiers)}"><span style="width:${(Math.min(1, fill) * 100).toFixed(1)}%"></span></div>
      <ol class="cg-progress__steps">${gift ? step(shipOk, SHOP.freeShippingFrom, T.freeShipping, 0.25) + step(giftOk, SHOP.giftFrom, T.gift, 0.75) : step(shipOk, SHOP.freeShippingFrom, T.freeShipping, 0.9)}</ol></div></div>`;
  };

  const lineHtml = (l) => {
    const media = `<a class="cg-line__media" href="${esc(l.product.url)}"><img src="${esc(l.variant.image || l.product.images[0] || '')}" alt="" width="84" height="84" loading="lazy"></a>`;
    const title = `<a class="cg-line__title" href="${esc(l.product.url)}">${esc(l.product.title)}</a>`;
    if (l.gift) {
      return `<li class="cg-line cg-line--gift" data-line="${esc(l.key)}">${media}
      <div class="cg-line__info">${title}</div>
      <p class="cg-line__price cg-line__price--inline">${l.total < l.compare ? `<s><span class="visually-hidden">${T.regularPrice}</span>${money(l.compare)}</s>` : ''}<span class="visually-hidden">${T.salePrice}</span>${money(l.total)}</p>
    </li>`;
    }
    const plans = l.product.plans;
    const planTag = l.plan
      ? `<label class="cg-line__plan"><span class="cg-line__plan-off">−${l.plan.discount}&nbsp;%</span><span class="cg-line__plan-select">
          <select data-line-plan="${esc(l.key)}" aria-label="${esc(T.frequency)}">${plans.map((p) => `<option value="${esc(p.id)}"${p === l.plan ? ' selected' : ''}>${esc(p.short)}</option>`).join('')}</select>${icon.chevron}</span></label>`
      : '';
    // abonnement : « Repasser en achat unique » ; achat unique d'un produit à abonnement : encart « Je m'abonne »
    const sub = l.plan
      ? `<button type="button" class="cg-line__switch" data-line-plan-off="${esc(l.key)}">${T.oneTime}</button>`
      : plans.length
        ? `<div class="cg-line__offer"><div class="cg-line__offer-copy"><p class="cg-line__offer-title">${tx('subOfferTitle', { pct: plans[0].discount })}</p><p class="cg-line__offer-note">${T.subOfferNote}</p></div>
          <button type="button" class="cg-line__offer-btn" data-line-plan-on="${esc(l.key)}" data-plan="${esc(plans[1]?.id ?? plans[0].id)}">${T.subOfferButton}<span class="visually-hidden">, ${tx('subscribe', { pct: plans[0].discount })}</span></button></div>`
        : '';
    return `<li class="cg-line" data-line="${esc(l.key)}">${media}
      <div class="cg-line__info">${title}${variantLabel(l.variant) ? `<p class="cg-line__variant">${esc(variantLabel(l.variant))}</p>` : ''}</div>
      <p class="cg-line__price">${l.plan ? `<span class="visually-hidden">${T.salePrice}</span>` : ''}${money(l.total)}${l.plan ? `<s><span class="visually-hidden">${T.regularPrice}</span>${money(l.compare)}</s>` : ''}</p>
      <div class="cg-line__controls">
        <div class="cg-qty">
          <button type="button" data-line-qty="${esc(l.key)}" data-step="-1"><span class="visually-hidden">${tx('decrease', { produit: esc(l.product.title) })}</span>${icon.minus}</button>
          <input type="number" min="0" max="99" value="${l.qty}" inputmode="numeric" aria-label="${tx('qtyOf', { produit: esc(l.product.title) })}" data-line-input="${esc(l.key)}">
          <button type="button" data-line-qty="${esc(l.key)}" data-step="1"><span class="visually-hidden">${tx('increase', { produit: esc(l.product.title) })}</span>${icon.plus}</button>
        </div>
        ${planTag}
        <button type="button" class="cg-line__remove" data-line-remove="${esc(l.key)}">${icon.trash}<span class="visually-hidden">${tx('remove', { produit: esc(l.product.title) })}</span></button>
      </div>
      ${sub ? `<div class="cg-line__sub">${sub}</div>` : ''}
    </li>`;
  };

  const upsellHtml = (lines) => {
    const inCart = new Set(lines.map((l) => l.product.handle));
    let items = SHOP.products.filter((p) => !inCart.has(p.handle) && p.variants.some((v) => v.available));
    // <site>/complements-exclus.json : produits jamais proposés ici
    const noUpsell = (SHOP.drawer && SHOP.drawer.upsellExcluded) || [];
    if (noUpsell.length) items = items.filter((p) => !noUpsell.includes(p.handle));
    // shop --complements <n> : les n accessoires (produits payants les moins chers) plutôt que tout le catalogue
    const max = SHOP.drawer && SHOP.drawer.upsell;
    if (max) {
      const low = (p) => Math.min(...p.variants.filter((v) => v.available).map((v) => v.price));
      items = items.filter((p) => low(p) > 0).sort((a, b) => low(a) - low(b)).slice(0, max);
    }
    if (!items.length) return '';
    return `<div class="cg-upsell"><p class="cg-upsell__title">${T.upsell}</p><ul class="cg-upsell__list">${items.map((p) => {
      const v = p.variants.find((x) => x.available);
      return `<li class="cg-upsell__item"><a class="cg-upsell__media" href="${esc(p.url)}" tabindex="-1"><img src="${esc(p.images[0] || '')}" alt="" width="92" height="92" loading="lazy"></a>
        <div class="cg-upsell__body"><a class="cg-upsell__name" href="${esc(p.url)}">${esc(p.title)}</a><span class="cg-upsell__price">${money(v.price)}</span>
        <button type="button" class="cg-upsell__add" data-add-variant="${esc(v.id)}">${icon.plus}<span class="visually-hidden">${tx('addProduct', { produit: esc(p.title) })}</span></button></div></li>`;
    }).join('')}</ul></div>`;
  };

  // tiroir : paliers dans l'en-tête (withProgress false) ; page panier : en tête du corps
  const cartBodyHtml = (t, withProgress = true) => {
    if (!t.lines.length) {
      return `<div class="cg-empty"><p class="cg-empty__title">${T.empty}</p><a class="cg-empty__btn" href="${esc(ROUTES.collection)}">${T.continueShopping}</a></div>`;
    }
    const paid = t.lines.filter((l) => !l.gift);
    const gifts = t.lines.filter((l) => l.gift);
    return `${withProgress ? progressHtml(t) : ''}<ul class="cg-lines">${paid.map(lineHtml).join('')}</ul>${gifts.length ? `<p class="cg-lines__group">${T.gift}</p><ul class="cg-lines cg-lines--gift">${gifts.map(lineHtml).join('')}</ul>` : ''}`;
  };

  const footerHtml = (t, withNote = true) => t.lines.length ? `<div class="cg-cart-foot">
      ${SHOP.drawer && SHOP.drawer.total === false ? '' : `<p class="cg-cart-foot__total"><span>${T.estimated}</span><span>${money(t.subtotal)}</span></p>`}
      <a class="cg-checkout-btn" href="${esc(ROUTES.checkout)}" data-checkout-link><span>${T.checkout}</span><span class="cg-checkout-btn__sep" aria-hidden="true">·</span>${t.compare > t.subtotal ? `<s><span class="visually-hidden">${T.regularPrice}</span>${money(t.compare)}</s>` : ''}<span>${money(t.subtotal)}</span></a>
      ${withNote ? `<p class="cg-cart-foot__note">${T.taxNote}</p>` : ''}
    </div>` : '';

  // Actions communes (délégation sur le conteneur)
  const bindCartActions = (root) => {
    root.addEventListener('click', (e) => {
      const t = e.target.closest('button');
      if (!t) return;
      if (t.dataset.lineQty) {
        const line = cart.raw().find((l) => l.key === t.dataset.lineQty);
        if (line) cart.setQty(line.key, line.qty + Number(t.dataset.step));
      } else if (t.dataset.lineRemove) cart.setQty(t.dataset.lineRemove, 0);
      else if (t.dataset.linePlanOff) cart.setPlan(t.dataset.linePlanOff, null);
      else if (t.dataset.linePlanOn) cart.setPlan(t.dataset.linePlanOn, t.dataset.plan);
      else if (t.dataset.addVariant) cart.add(t.dataset.addVariant, 1).catch(() => {});
      else return;
      e.stopPropagation();
    });
    root.addEventListener('change', (e) => {
      const el = e.target;
      if (el.dataset.linePlan) cart.setPlan(el.dataset.linePlan, el.value);
      if (el.dataset.lineInput) cart.setQty(el.dataset.lineInput, Math.max(0, parseInt(el.value, 10) || 0));
    });
  };

  // Garde le focus sur le même contrôle après un nouveau rendu
  const rerender = (root, html) => {
    const active = document.activeElement;
    const sel = active && root.contains(active)
      ? ['data-line-qty', 'data-line-remove', 'data-line-plan', 'data-line-input', 'data-add-variant'].map((a) => active.hasAttribute(a) ? `[${a}="${CSS.escape(active.getAttribute(a))}"]${active.dataset.step ? `[data-step="${active.dataset.step}"]` : ''}` : '').find(Boolean)
      : null;
    root.innerHTML = html;
    const again = sel && $(sel, root);
    (again || (sel && $('.cg-drawer__close, h1', root.closest('.cg-drawer, main') || document)))?.focus?.();
  };

  /* ---------------------------------------------------------------------
     Tiroir panier
     --------------------------------------------------------------------- */
  let drawer = null;
  let lastFocus = null;
  const buildDrawer = () => {
    document.body.insertAdjacentHTML('beforeend', `
      <div class="cg-drawer__backdrop" data-cart-close></div>
      <aside class="cg-drawer" id="cg-cart-drawer" role="dialog" aria-modal="true" aria-labelledby="cg-cart-drawer-title" inert>
        <div class="cg-drawer__head">
          <div class="cg-drawer__top">
            <div class="cg-drawer__title-row">
              <h2 class="cg-drawer__title" id="cg-cart-drawer-title">${T.cart}</h2>
              ${SHOP.freeShippingFrom > 0 ? `<p class="cg-drawer__perk"><span>${SHOP.products.some((p) => p.plans.length) ? T.perk : tx('perkFrom', { montant: moneyShort(SHOP.freeShippingFrom) })}</span></p>` : ''}
            </div>
            <button type="button" class="cg-drawer__close" data-cart-close>${icon.close}<span class="visually-hidden">${T.close}</span></button>
          </div>
          <div data-cart-progress></div>
        </div>
        <div class="cg-drawer__body">
          <div data-cart-body></div>
          <div class="cg-drawer__extra" data-cart-upsell></div>
        </div>
        <div data-cart-foot></div>
      </aside>`);
    drawer = $('#cg-cart-drawer');
    bindCartActions(drawer);
    $$('[data-cart-close]').forEach((b) => b.addEventListener('click', () => setDrawer(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) setDrawer(false); });
    drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const items = $$('a[href], button:not([disabled]), select, input', drawer).filter((x) => x.offsetParent !== null);
      if (!items.length) return;
      if (e.shiftKey && document.activeElement === items[0]) { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === items[items.length - 1]) { e.preventDefault(); items[0].focus(); }
    });
  };
  const renderDrawer = () => {
    if (!drawer) return;
    const t = cart.totals();
    drawer.classList.toggle('is-empty', !t.lines.length);
    $('[data-cart-progress]', drawer).innerHTML = t.lines.length ? progressHtml(t) : '';
    rerender($('[data-cart-body]', drawer), cartBodyHtml(t, false));
    $('[data-cart-upsell]', drawer).innerHTML = t.lines.length ? upsellHtml(t.lines) : '';
    $('[data-cart-foot]', drawer).innerHTML = footerHtml(t, false);
  };
  const setDrawer = (open) => {
    if (!drawer) return;
    if (open) { renderDrawer(); lastFocus = document.activeElement; }
    drawer.classList.toggle('is-open', open);
    drawer.toggleAttribute('inert', !open);
    $('.cg-drawer__backdrop').classList.toggle('is-open', open);
    document.documentElement.classList.toggle('cart-open', open);
    $$('[data-cart-open]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) setTimeout(() => $('.cg-drawer__close', drawer).focus(), 60);
    else if (lastFocus) { lastFocus.focus(); lastFocus = null; }
  };

  /* ---------------------------------------------------------------------
     Page panier (cart.html)
     --------------------------------------------------------------------- */
  const renderCartPage = (root) => {
    const t = cart.totals();
    rerender($('[data-cart-body]', root), cartBodyHtml(t));
    $('[data-cart-upsell]', root).innerHTML = t.lines.length ? upsellHtml(t.lines) : '';
    $('[data-cart-foot]', root).innerHTML = footerHtml(t);
  };

  /* ---------------------------------------------------------------------
     Boutons « ajouter au panier » génériques :
     [data-add-variant="id"] (+ data-plan, data-qty) ou formulaire [data-product-form]
     --------------------------------------------------------------------- */
  const flash = (btn, text) => {
    if (!btn || btn.closest('.cg-drawer')) return;
    const label = btn.querySelector('[data-add-label]') || btn;
    const old = label.innerHTML;
    btn.classList.add('is-added');
    label.textContent = text;
    setTimeout(() => { btn.classList.remove('is-added'); label.innerHTML = old; }, 1400);
  };
  // fin d'un ajout : « Ajouté » et tiroir ouvert, ou « Indisponible » si Shopify refuse
  const added = (btn, promise) => promise.then(() => { flash(btn, T.added); setDrawer(true); }, () => flash(btn, T.unavailable));
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-variant]');
    // un bouton du tiroir déjà traité (et retiré du DOM par le nouveau rendu) n'est pas rejoué
    if (!btn || !btn.isConnected || btn.closest('.cg-drawer, [data-cart-page]')) return;
    e.preventDefault();
    added(btn, cart.add(btn.dataset.addVariant, Number(btn.dataset.qty) || 1, btn.dataset.plan || null));
  });
  /* ---------------------------------------------------------------------
     Adaptateur des pages reconstruites : reconnaît les blocs d'achat et les
     boutons « Ajouter » d'après leurs textes (titres, saveurs, formats),
     sans attribut propre au panier (une reconstruction ne les effacera pas).
     --------------------------------------------------------------------- */
  const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, ' ').trim();
  const hasWord = (hay, needle) => new RegExp(`(^|[^a-z0-9])${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`).test(hay);
  const currentFile = decodeURIComponent(location.pathname.split('/').pop() || '') || 'index.html';
  const here = location.pathname.replace(/\/+$/, '');
  const pageProduct = SHOP.products.find((p) => p.url === currentFile || (p.url.startsWith('/') && here.endsWith(p.url)));
  const titlesLongestFirst = [...SHOP.products].sort((a, b) => b.title.length - a.title.length);
  // un titre porté par plusieurs produits (traduction, variantes de pack) ne désigne aucun produit en particulier
  const titleCount = {};
  SHOP.products.forEach((p) => { const t = norm(p.title); titleCount[t] = (titleCount[t] || 0) + 1; });
  const productIn = (text) => { const n = norm(text); return titlesLongestFirst.find((p) => titleCount[norm(p.title)] === 1 && n.includes(norm(p.title))) || null; };
  // produit désigné par un lien vers sa fiche dans l'élément (le titre du catalogue n'est pas toujours celui du site, une fois traduit)
  const productByLink = (el) => {
    for (const a of $$('a[href]', el)) {
      const file = decodeURIComponent((a.getAttribute('href') || '').split('#')[0].split('?')[0]);
      const p = SHOP.products.find((x) => x.url === file);
      if (p) return p;
    }
    return null;
  };
  const textOf = (el) => (el ? `${el.getAttribute('aria-label') || ''} ${el.textContent || ''} ${$$('img', el).map((i) => i.alt).join(' ')}` : '');
  const pickVariant = (product, hay) => {
    const n = norm(hay);
    const pool = product.variants.filter((v) => v.available);
    let best = null;
    let bestScore = -1;
    for (const v of pool.length ? pool : product.variants) {
      const score = v.options.filter((o) => o !== 'Default Title' && hasWord(n, norm(o))).length;
      if (score > bestScore) { best = v; bestScore = score; }
    }
    return best;
  };
  // choix faits dans un bloc d'achat : radios et cases cochées, listes, boutons à état (modèle TM6, taille…)
  const choicesIn = (scope) => [
    ...$$('input[type="radio"]:checked, input[type="checkbox"]:checked', scope).map((i) => `${i.value} ${i.closest('label') ? i.closest('label').textContent : ''}`),
    ...$$('select:not([data-cg-item])', scope).map((s) => (s.selectedOptions[0] ? s.selectedOptions[0].textContent : '')),
    ...$$('button[aria-pressed="true"]', scope).map((b) => b.textContent),
  ].join(' | ');
  // offre choisie d'un sélecteur (élément data-cg-bundle à l'état choisi) : ses produits, celui de la fiche en premier
  const chosenOffer = (scope) => $$('[data-cg-bundle]', scope).find((x) => x.matches('.is-selected, .is-on, .is-active, [aria-checked="true"], input:checked') || $('input[type="radio"]:checked', x));
  // modèle d'un article de l'offre choisie (liste propre à la ligne : <select data-cg-item="poignée">)
  const itemChoice = (scope, product) => {
    const sel = $$('select[data-cg-item]', chosenOffer(scope) || scope).find((x) => x.getAttribute('data-cg-item') === product.handle);
    return sel && sel.value && sel.selectedOptions[0] ? sel.selectedOptions[0].textContent : '';
  };
  const bundleIn = (scope) => {
    const el = chosenOffer(scope);
    const list = el ? el.getAttribute('data-cg-bundle').split(',').map((h) => byHandle.get(h.trim())).filter(Boolean) : [];
    return list.length > 1 ? list : null;
  };
  // modèle à choisir (groupe de boutons à état de l'offre choisie, aucun pressé) : rien n'est ajouté, la page le demande
  // message visible sous le groupe (le message de la page n'était lu que par les lecteurs d'écran), retiré dès qu'un choix est fait
  const choiceMissing = (scope) => {
    const host = chosenOffer(scope) || scope;
    // groupes de l'offre choisie, et ceux du bloc d'achat hors des cartes d'offre (modèle choisi une fois pour toutes)
    const outside = host === scope ? [] : $$('[role="group"], [role="radiogroup"]', scope).filter((g) => !g.closest('[data-cg-bundle]'));
    const groups = [...$$('[role="group"], [role="radiogroup"]', host), ...outside].filter((g) => $$('button[aria-pressed]', g).length > 1);
    const empty = [...groups.filter((g) => !$('button[aria-pressed="true"]', g)), ...$$('select[data-cg-item]', host).filter((x) => !x.value)];
    empty.forEach((g) => {
      const row = g.parentElement && g.parentElement.children.length <= 2 ? g.parentElement : g;
      if (!(row.nextElementSibling && row.nextElementSibling.classList.contains('cg-choice-error'))) row.insertAdjacentHTML('afterend', '<p class="cg-choice-error" role="alert"></p>');
      row.nextElementSibling.textContent = T.chooseOption;
    });
    if (empty[0]) empty[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    return empty.length > 0;
  };
  const clearChoiceError = (g) => {
    const row = g.parentElement && g.parentElement.children.length <= 2 ? g.parentElement : g;
    if (row.nextElementSibling && row.nextElementSibling.classList.contains('cg-choice-error')) row.nextElementSibling.remove();
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('button[aria-pressed]');
    const g = b && b.closest('[role="group"], [role="radiogroup"]');
    if (g) clearChoiceError(g);
  });
  document.addEventListener('change', (e) => { if (e.target.matches && e.target.matches('select[data-cg-item]') && e.target.value) clearChoiceError(e.target); });
  const qtyIn = (scope) => { const q = $('input[name="quantity"], [data-qty-input], input[type="number"]', scope); return Math.max(1, parseInt(q ? q.value : '1', 10) || 1); };
  const addBundle = (btn, products, scope) => {
    const choices = choicesIn(scope);
    const qty = qtyIn(scope);
    const variants = products.map((p) => pickVariant(p, `${itemChoice(scope, p)} | ${choices}`));
    // offre à article remisé : sa variante du pack, nommée par les modèles choisis (articles à plusieurs modèles, dans l'ordre)
    const offer = chosenOffer(scope);
    const pack = offer && PACKS.find((p) => p.handle === offer.getAttribute('data-cg-pack'));
    if (pack) {
      const title = variants.filter((v, i) => products[i].variants.length > 1).map((v) => v.title).join(' + ') || 'Default Title';
      const v = pack.variants.find((x) => x.title === title);
      if (v) { added(btn, cart.add(v.id, qty, null)); return; }
    }
    added(btn, cart.addMany(variants.map((v) => ({ variantId: v.id, qty }))));
  };

  // « Ajouter », « Add to cart », « Dodaj do koszyka », « In den Warenkorb »… et le texte d'ajout traduit du site
  const ADD_WORDS = /^(ajout|add\b|add to|dodaj|in den warenkorb|zum warenkorb|hinzufug|aggiungi|anadir|agregar|adicionar|toevoegen|in winkelwagen|lagg|tilfoj|legg i|lisaa|pridat|kosarba|adauga|sepete|v korzin|do koszyka)/;
  const isAddLabel = (el) => { const n = norm(el.getAttribute('aria-label') || el.textContent); return ADD_WORDS.test(n) || n.startsWith(norm(T.addToCart).split(' ')[0]); };

  // Formulaires d'achat (bouton « Ajouter au panier »)
  document.addEventListener('submit', (e) => {
    const form = e.target;
    const submit = form.querySelector('[type="submit"]');
    if (!submit || !isAddLabel(submit) || form.closest('[data-cart-page], .cg-drawer')) return;
    e.preventDefault();
    const scope = form.closest('section, article, li') || form;
    const product = pageProduct || productIn(textOf(scope)) || productByLink(scope) || byHandle.get(form.getAttribute('data-product') || '');
    if (!product || choiceMissing(form)) return;
    const bundle = bundleIn(form);
    if (bundle) { addBundle(submit, bundle, form); return; }
    const choices = choicesIn(form);
    const variant = pickVariant(product, choices);
    let plan = null;
    const planInput = $$('input[type="radio"]:checked', form).find((i) => /abonn|achat unique/.test(norm(i.closest('label') ? i.closest('label').textContent : i.value)));
    if (planInput && /abonn/.test(norm(planInput.closest('label').textContent)) && product.plans.length) {
      const freq = $('select[name="frequency"]', form) || $('select', planInput.closest('label') || form);
      const label = freq && freq.selectedOptions[0] ? norm(freq.selectedOptions[0].textContent) : '';
      // fréquence en mois (valeur de l'option) : la formule du même rythme, avec sa remise
      plan = (freq && product.plans.find((p) => p.months && p.months === Number(freq.value))) || product.plans.find((p) => norm(p.name) === label) || product.plans.find((p) => /mensuel/i.test(p.name)) || product.plans[0];
    }
    const qtyInput = $('input[name="quantity"], input[type="number"]', form);
    // paliers de quantité (pack de 2, 3, 6 : bouton radio à valeur entière et prix par palier) : la quantité est celle du palier
    const tier = $$('input[type="radio"]:checked', form).find((i) => /^\d+$/.test(i.value) && i.hasAttribute('data-price'));
    added(submit, cart.add(variant.id, Math.max(1, parseInt(qtyInput ? qtyInput.value : tier ? tier.value : '1', 10) || 1), plan ? plan.id : null));
  });

  // Boutons « Ajouter », « Ajout rapide », « Ajouter au panier : … »
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button');
    if (!btn || !btn.isConnected || btn.type === 'submit' || btn.hasAttribute('data-add-variant') || btn.closest('.cg-drawer, [data-cart-page], form[data-checkout-form]') || !isAddLabel(btn)) return;
    // premier parent qui nomme un produit (la carte, pas la grille entière)
    // lien vers le produit d'abord (précis), puis la fiche de la page, puis le titre cité (ambigu si des titres sont identiques)
    let product = productIn(btn.getAttribute('aria-label') || '');
    let scope = btn.parentElement;
    const card = btn.closest('li, article, [class*="card"]');
    if (!product && card) { product = productByLink(card); scope = card; }
    if (!product) product = pageProduct;
    if (!product) for (let el = btn.parentElement, i = 0; el && i < 6 && !product; el = el.parentElement, i++) { product = productByLink(el); scope = el; }
    for (let el = btn.parentElement, i = 0; el && i < 6 && !product; el = el.parentElement, i++) { product = productIn(textOf(el)); scope = el; }
    if (!product) return;
    e.preventDefault();
    // bouton d'un bloc d'achat (formulaire de la fiche) : offre groupée, modèle et quantité choisis
    const form = btn.closest('form');
    if (form && product === pageProduct) {
      if (choiceMissing(form)) return;
      const bundle = bundleIn(form);
      if (bundle) { addBundle(btn, bundle, form); return; }
      added(btn, cart.add(pickVariant(product, choicesIn(form)).id, qtyIn(form), null));
      return;
    }
    const selected = $$('select', scope).map((s) => (s.selectedOptions[0] ? s.selectedOptions[0].textContent : '')).join(' | ');
    // carte d'une formule précise : data-variant-name = nom de l'option du produit (« Essentiel »)
    added(btn, cart.add(pickVariant(product, `${selected} | ${btn.getAttribute('data-variant-name') || ''}`).id, 1, null));
  });

  // Prix des blocs d'achat (data-price / data-sub des packs) alignés sur le catalogue :
  // dans Shopify, un prix modifié dans l'administration s'affiche sans toucher au thème.
  const syncBuyForms = () => {
    $$('form[data-buy-form]').forEach((form) => {
      const product = pageProduct || productIn(textOf(form.closest('section') || form));
      if (!product) return;
      const plan = product.plans.find((p) => /mensuel/i.test(p.name)) || product.plans[0] || null;
      let changed = false;
      $$('input[name="pack"]', form).forEach((input) => {
        const v = pickVariant(product, `${input.value} ${input.closest('label') ? input.closest('label').textContent : ''}`);
        if (!v) return;
        const price = amount(v.price);
        const sub = amount(unitPrice(v, plan));
        if (input.dataset.price !== price || input.dataset.sub !== sub) { input.dataset.price = price; input.dataset.sub = sub; changed = true; }
      });
      const checked = $('input[name="pack"]:checked', form);
      if (changed && checked) checked.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  // API pour les scripts de page (fiche produit, carte produit…)
  window.CGCart = {
    add(variantId, qty = 1, planId = null, { open = true } = {}) { return cart.add(variantId, qty, planId).then(() => { if (open) setDrawer(true); }); },
    open: () => setDrawer(true),
    close: () => setDrawer(false),
    lines: () => cart.lines(),
    count: () => cart.count(),
    clear: () => cart.clear(),
    product: (handle) => byHandle.get(handle),
    variantFor(handle, values) {
      const p = byHandle.get(handle);
      return p && p.variants.find((v) => values.every((val, i) => !val || v.options[i] === val));
    },
    money, euros,
  };

  /* ---------------------------------------------------------------------
     Passage en caisse (démonstration)
     --------------------------------------------------------------------- */
  const ORDERS_KEY = 'cg-orders';
  const CUSTOMER_KEY = 'cg-customer';
  const PROFILE_KEY = 'cg-profile';
  const POSTAL = { France: /^\d{5}$/, Belgique: /^\d{4}$/, Luxembourg: /^(L-?)?\d{4}$/i, Suisse: /^\d{4}$/ };
  const luhn = (n) => {
    let sum = 0;
    [...n].reverse().forEach((d, i) => { let x = Number(d); if (i % 2) { x *= 2; if (x > 9) x -= 9; } sum += x; });
    return sum % 10 === 0;
  };

  const summaryHtml = (t) => `
    <ul class="co-items">${t.lines.map((l) => `<li class="co-item">
      <span class="co-item__media"><img src="${esc(l.variant.image || l.product.images[0] || '')}" alt="" width="64" height="64"><span class="co-item__qty">${l.qty}</span></span>
      <span class="co-item__info"><span class="co-item__title">${esc(l.product.title)}</span>${variantLabel(l.variant) ? `<span class="co-item__meta">${esc(variantLabel(l.variant))}</span>` : ''}${l.plan ? `<span class="co-item__meta">${esc(l.plan.name)} · −${l.plan.discount} %</span>` : ''}</span>
      <span class="co-item__price">${l.plan ? `<s>${euros(l.compare)}</s>` : ''}${euros(l.total)}</span></li>`).join('')}</ul>
    <form class="co-discount" data-discount novalidate>
      <label class="co-field"><input type="text" name="code" placeholder=" " autocomplete="off"><span>${T.discountCode}</span></label>
      <button type="submit" class="co-btn-secondary">Valider</button>
      <p class="co-error" data-discount-error hidden></p>
    </form>
    <dl class="co-totals">
      <div><dt>${T.subtotal} · ${cart.count()} ${cart.count() > 1 ? T.items : T.item}</dt><dd>${euros(t.subtotal)}</dd></div>
      <div><dt>${T.shipping}</dt><dd>${t.freeShipping ? T.free : euros(t.shipping)}</dd></div>
      <div class="co-totals__grand"><dt>${T.total}</dt><dd><small>EUR</small> ${euros(t.total)}</dd></div>
    </dl>`;

  const initCheckout = (form) => {
    const summary = $('[data-checkout-summary]');
    const toggleTotal = $('[data-summary-total]');
    const emptyBox = $('[data-checkout-empty]');
    const render = () => {
      const t = cart.totals();
      const empty = !t.lines.length;
      form.hidden = empty;
      if (emptyBox) emptyBox.hidden = !empty;
      $$('[data-checkout-filled]').forEach((el) => { el.hidden = empty; });
      if (summary) summary.innerHTML = summaryHtml(t);
      if (toggleTotal) toggleTotal.textContent = euros(t.total);
      $$('[data-shipping-price]').forEach((el) => { el.textContent = t.freeShipping ? 'GRATUIT' : euros(t.shipping); });
      $$('[data-pay-total]').forEach((el) => { el.textContent = euros(t.total); });
      const discount = $('[data-discount]');
      discount && discount.addEventListener('submit', (e) => {
        e.preventDefault();
        const code = discount.code.value.trim();
        const err = $('[data-discount-error]', discount);
        err.hidden = false;
        err.textContent = code ? T.discountInvalid : T.discountEmpty;
      });
    };
    render();
    cart.onChange(render);

    // préremplissage depuis le profil et le compte
    const profile = store.get(PROFILE_KEY, null);
    const customer = store.get(CUSTOMER_KEY, null);
    if (customer && !form.email.value) form.email.value = customer.email;
    if (profile && profile.address) {
      for (const [k, v] of Object.entries(profile.address)) if (form[k] && !form[k].value) form[k].value = v;
    }

    // moyens de paiement
    const methods = $$('input[name="payment"]', form);
    const syncPayment = () => {
      const m = form.payment.value;
      $$('[data-payment-panel]', form).forEach((p) => { p.hidden = p.dataset.paymentPanel !== m; $$('input', p).forEach((i) => { i.disabled = p.hidden; }); });
      const label = { card: T.payCard, paypal: T.payPaypal, klarna: T.payKlarna }[m];
      $('[data-pay-label]', form).textContent = label;
    };
    methods.forEach((m) => m.addEventListener('change', syncPayment));
    syncPayment();

    // formats de saisie
    const cardNumber = form.cardNumber;
    cardNumber && cardNumber.addEventListener('input', () => { cardNumber.value = cardNumber.value.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 '); });
    const expiry = form.cardExpiry;
    expiry && expiry.addEventListener('input', () => { const d = expiry.value.replace(/\D/g, '').slice(0, 4); expiry.value = d.length > 2 ? `${d.slice(0, 2)} / ${d.slice(2)}` : d; });

    const setError = (input, msg) => {
      const field = input.closest('.co-field') || input.parentElement;
      let err = field.nextElementSibling && field.nextElementSibling.matches('.co-error') ? field.nextElementSibling : null;
      if (msg) {
        if (!err) { err = document.createElement('p'); err.className = 'co-error'; err.id = `${input.name}-error`; field.after(err); }
        err.textContent = msg;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', err.id);
      } else {
        err && err.remove();
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    };

    const rules = {
      email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : v ? T.emailInvalid : T.emailEmpty),
      lastName: (v) => (v ? '' : T.lastName),
      address1: (v) => (v ? '' : T.address),
      postalCode: (v) => (!v ? T.postal : POSTAL[form.country.value] && !POSTAL[form.country.value].test(v.replace(/\s/g, '')) ? tx('postalInvalid', { pays: form.country.value }) : ''),
      city: (v) => (v ? '' : T.city),
      phone: (v) => (!v || /^[+\d][\d\s.-]{7,}$/.test(v) ? '' : T.phone),
      cardNumber: (v) => { const d = v.replace(/\s/g, ''); return !d ? T.cardEmpty : d.length < 13 || !luhn(d) ? T.cardInvalid : ''; },
      cardExpiry: (v) => {
        const m = /^(\d{2}) \/ (\d{2})$/.exec(v);
        if (!m) return v ? T.expiryInvalid : T.expiryEmpty;
        const month = Number(m[1]); const year = 2000 + Number(m[2]);
        const now = new Date();
        if (month < 1 || month > 12) return T.expiryInvalid;
        return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1) ? T.expired : '';
      },
      cardCvc: (v) => (/^\d{3,4}$/.test(v) ? '' : v ? T.cvcInvalid : T.cvcEmpty),
      cardName: (v) => (v ? '' : T.cardName),
    };
    const check = (input) => { const rule = rules[input.name]; if (!rule || input.disabled) return true; const msg = rule(input.value.trim()); setError(input, msg); return !msg; };
    Object.keys(rules).forEach((name) => { const input = form[name]; input && input.addEventListener('blur', () => input.value && check(input)); });
    form.country.addEventListener('change', () => form.postalCode.value && check(form.postalCode));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const inputs = Object.keys(rules).map((n) => form[n]).filter(Boolean);
      const bad = inputs.filter((i) => !check(i));
      if (bad.length) { bad[0].focus(); return; }
      const t = cart.totals();
      if (!t.lines.length) return;
      const number = `CG${Math.floor(1000 + Math.random() * 9000)}`;
      const order = {
        number,
        date: new Date().toISOString(),
        email: form.email.value.trim(),
        address: { firstName: form.firstName.value.trim(), lastName: form.lastName.value.trim(), address1: form.address1.value.trim(), address2: form.address2.value.trim(), postalCode: form.postalCode.value.trim(), city: form.city.value.trim(), country: form.country.value, phone: form.phone.value.trim() },
        payment: form.payment.value === 'card' ? `Carte se terminant par ${form.cardNumber.value.replace(/\s/g, '').slice(-4)}` : form.payment.value === 'paypal' ? 'PayPal' : 'Klarna',
        lines: t.lines.map((l) => ({ title: l.product.title, variant: variantLabel(l.variant), plan: l.plan ? l.plan.name : null, qty: l.qty, total: l.total, image: l.variant.image || l.product.images[0] || '', url: l.product.url })),
        subtotal: t.subtotal, shipping: t.shipping, total: t.total,
      };
      store.set(ORDERS_KEY, [order, ...store.get(ORDERS_KEY, [])]);
      if (form.saveInfo && form.saveInfo.checked) store.set(PROFILE_KEY, { ...(store.get(PROFILE_KEY, {}) || {}), address: { ...order.address } });
      const btn = $('[type="submit"]', form);
      btn.disabled = true;
      $('[data-pay-label]', form).textContent = 'Traitement…';
      setTimeout(() => { cart.clear(); location.href = `checkout-thank-you.html?commande=${number}`; }, 700);
    });
  };

  const orderHtml = (o) => `
    <ul class="co-items">${o.lines.map((l) => `<li class="co-item"><span class="co-item__media"><img src="${esc(l.image)}" alt="" width="64" height="64"><span class="co-item__qty">${l.qty}</span></span>
      <span class="co-item__info"><a class="co-item__title" href="${esc(l.url)}">${esc(l.title)}</a>${l.variant ? `<span class="co-item__meta">${esc(l.variant)}</span>` : ''}${l.plan ? `<span class="co-item__meta">${esc(l.plan)}</span>` : ''}</span>
      <span class="co-item__price">${euros(l.total)}</span></li>`).join('')}</ul>
    <dl class="co-totals"><div><dt>${T.subtotal}</dt><dd>${euros(o.subtotal)}</dd></div><div><dt>${T.shipping}</dt><dd>${o.shipping ? euros(o.shipping) : T.free}</dd></div><div class="co-totals__grand"><dt>${T.total}</dt><dd><small>EUR</small> ${euros(o.total)}</dd></div></dl>`;

  const initThankYou = (root) => {
    const num = new URLSearchParams(location.search).get('commande');
    const order = store.get(ORDERS_KEY, []).find((o) => o.number === num) || store.get(ORDERS_KEY, [])[0];
    if (!order) { $('[data-order-missing]', root).hidden = false; return; }
    $('[data-order-found]', root).hidden = false;
    $('[data-order-number]', root).textContent = tx('orderNumber', { n: order.number });
    $('[data-order-name]', root).textContent = order.address.firstName ? `Merci, ${order.address.firstName} !` : 'Merci !';
    $('[data-order-email]', root).textContent = order.email;
    const a = order.address;
    $('[data-order-address]', root).innerHTML = [`${a.firstName} ${a.lastName}`.trim(), a.address1, a.address2, `${a.postalCode} ${a.city}`, a.country, a.phone].filter(Boolean).map(esc).join('<br>');
    $('[data-order-payment]', root).textContent = `${order.payment} · ${euros(order.total)}`;
    $('[data-order-summary]', root).innerHTML = orderHtml(order);
  };

  /* ---------------------------------------------------------------------
     Compte client (démonstration : connexion par code, commandes, profil)
     --------------------------------------------------------------------- */
  const initLogin = (root) => {
    const form = $('[data-login-form]', root);
    const step2 = $('[data-login-code]', root);
    let email = '';
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.email;
      const err = $('[data-login-error]', form);
      email = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { err.hidden = false; err.textContent = email ? T.emailInvalid : T.emailEmpty; input.focus(); return; }
      err.hidden = true;
      form.hidden = true;
      step2.hidden = false;
      $('[data-login-email]', step2).textContent = email;
      step2.code.focus();
    });
    step2.addEventListener('submit', (e) => {
      e.preventDefault();
      const err = $('[data-login-error]', step2);
      if (!/^\d{6}$/.test(step2.code.value.trim())) { err.hidden = false; err.textContent = T.code; step2.code.focus(); return; }
      store.set(CUSTOMER_KEY, { email });
      location.href = new URLSearchParams(location.search).get('retour') || 'account.html';
    });
    $('[data-login-back]', step2).addEventListener('click', () => { step2.hidden = true; form.hidden = false; form.email.focus(); });
  };

  const requireCustomer = () => {
    const c = store.get(CUSTOMER_KEY, null);
    if (!c) location.replace(`account-login.html?retour=${encodeURIComponent(location.pathname.split('/').pop())}`);
    return c;
  };

  const initAccount = (root) => {
    const c = requireCustomer();
    if (!c) return;
    $$('[data-customer-email]', root).forEach((el) => { el.textContent = c.email; });
    const orders = store.get(ORDERS_KEY, []).filter((o) => o.email.toLowerCase() === c.email.toLowerCase());
    const list = $('[data-orders]', root);
    list.innerHTML = orders.length
      ? orders.map((o) => `<li class="acc-order"><div class="acc-order__head"><strong>#${esc(o.number)}</strong><span>${new Date(o.date).toLocaleDateString(document.documentElement.lang || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span><span class="acc-order__status">${T.confirmed}</span><span>${euros(o.total)}</span></div>
          <ul class="acc-order__lines">${o.lines.map((l) => `<li><img src="${esc(l.image)}" alt="" width="48" height="48"><span>${l.qty} × ${esc(l.title)}${l.variant ? ` — ${esc(l.variant)}` : ''}</span></li>`).join('')}</ul></li>`).join('')
      : `<li class="acc-empty"><p>${T.noOrders}</p><a class="btn" href="${ROUTES.collection}">${T.startShopping}</a></li>`;
  };

  const initProfile = (root) => {
    const c = requireCustomer();
    if (!c) return;
    $$('[data-customer-email]', root).forEach((el) => { el.textContent = c.email; });
    const form = $('[data-profile-form]', root);
    const profile = store.get(PROFILE_KEY, {}) || {};
    const a = profile.address || {};
    for (const [k, v] of Object.entries(a)) if (form[k]) form[k].value = v;
    const view = $('[data-address-view]', root);
    const show = () => {
      const p = store.get(PROFILE_KEY, {}) || {};
      const x = p.address;
      view.innerHTML = x && x.address1
        ? `<p>${[`${x.firstName || ''} ${x.lastName || ''}`.trim(), x.address1, x.address2, `${x.postalCode} ${x.city}`, x.country, x.phone].filter(Boolean).map(esc).join('<br>')}</p>`
        : `<p>${T.noAddress}</p>`;
    };
    show();
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const missing = ['lastName', 'address1', 'postalCode', 'city'].find((n) => !form[n].value.trim());
      const msg = $('[data-profile-msg]', root);
      if (missing) { msg.hidden = false; msg.textContent = T.required; form[missing].focus(); return; }
      const address = Object.fromEntries(['firstName', 'lastName', 'address1', 'address2', 'postalCode', 'city', 'country', 'phone'].map((n) => [n, form[n].value.trim()]));
      store.set(PROFILE_KEY, { ...profile, address });
      msg.hidden = false;
      msg.textContent = T.addressSaved;
      show();
    });
    $('[data-address-delete]', root).addEventListener('click', () => {
      store.set(PROFILE_KEY, { ...profile, address: null });
      form.reset();
      show();
    });
  };

  $$('[data-logout]').forEach((b) => b.addEventListener('click', () => { store.set(CUSTOMER_KEY, null); location.href = 'index.html'; }));

  // Popover compte : adresse du client connecté (Shopify : compte client Shopify)
  const customer = SHOPIFY ? (SHOP.customer ? { email: SHOP.customer } : null) : store.get(CUSTOMER_KEY, null);
  if (customer) $$('.account__signin').forEach((a) => { a.href = ROUTES.account; a.lastChild.textContent = customer.email; });

  /* ---------------------------------------------------------------------
     Démarrage
     --------------------------------------------------------------------- */
  buildDrawer();
  renderBubble();
  syncBuyForms();
  if (SHOPIFY) refresh().catch(() => {});
  cart.onChange(() => { renderBubble(); renderDrawer(); });
  $$('[data-cart-open]').forEach((a) => {
    a.setAttribute('aria-haspopup', 'dialog');
    a.setAttribute('aria-controls', 'cg-cart-drawer');
    a.addEventListener('click', (e) => { e.preventDefault(); setDrawer(true); });
  });
  const page = $('[data-cart-page]');
  if (page) { bindCartActions(page); renderCartPage(page); cart.onChange(() => renderCartPage(page)); }
  const checkoutForm = $('[data-checkout-form]');
  if (checkoutForm) initCheckout(checkoutForm);
  const thanks = $('[data-thank-you]');
  if (thanks) initThankYou(thanks);
  const login = $('[data-login]');
  if (login) initLogin(login);
  const account = $('[data-account-page]');
  if (account) initAccount(account);
  const profilePage = $('[data-profile-page]');
  if (profilePage) initProfile(profilePage);
  // ouverture du panier demandée par une autre page (?panier=1)
  if (new URLSearchParams(location.search).get('panier') === '1') setDrawer(true);
})();
